import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/** Bubble-only `cart:update` from store selection; count is published after section morph. */
const SILENT_CART_UPDATE_SOURCES = new Set([
  'store-selector-drawer',
  'store-selector-drawer-after-close',
  'store-locator',
  'store-detail',
]);

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #countSyncGeneration = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = String(value);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const source = String(event.detail?.data?.source || '');
    if (SILENT_CART_UPDATE_SOURCES.has(source)) return;

    // A failed add (e.g. Shopify's "max in cart"/stock cap) still fires
    // `cart:update` with `didError: true` and the *requested* itemCount. Adding
    // that to the bubble desyncs the header from the real cart — and the wrong
    // number then gets cached in sessionStorage for 10s and re-applied on the
    // next navigation, which reads as "add to cart is inconsistent". The error
    // response carries no authoritative count, so re-sync from /cart.js instead.
    if (event.detail?.data?.didError) {
      void this.#syncFromServerCart(++this.#countSyncGeneration);
      return;
    }

    this.#countSyncGeneration += 1;
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Re-reads the authoritative cart count from `/cart.js` (never cached) and
   * sets the bubble to it. Used after a failed add so the header reflects what
   * the server actually accepted (which may be a partial add up to the cap).
   * @param {number} generation
   */
  #syncFromServerCart = async (generation) => {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (generation !== this.#countSyncGeneration) return;
      const count = Number(cart?.item_count);
      if (Number.isFinite(count) && count >= 0) {
        this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // no-op — leave the bubble untouched if the re-sync fetch fails.
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
